'use strict';

var w3cContext = require('@trustvc/w3c-context');
var w3cCredentialStatus = require('@trustvc/w3c-credential-status');
var w3cIssuer = require('@trustvc/w3c-issuer');
var crypto = require('crypto');
var jsonld = require('jsonld');
var uuid = require('uuid');
var credentialStatus = require('../sign/credentialStatus');
var types = require('../types');

function _interopNamespace(e) {
  if (e && e.__esModule) return e;
  var n = Object.create(null);
  if (e) {
    Object.keys(e).forEach(function (k) {
      if (k !== 'default') {
        var d = Object.getOwnPropertyDescriptor(e, k);
        Object.defineProperty(n, k, d.get ? d : {
          enumerable: true,
          get: function () { return e[k]; }
        });
      }
    });
  }
  n.default = e;
  return Object.freeze(n);
}

var jsonld__namespace = /*#__PURE__*/_interopNamespace(jsonld);

var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
function _checkKeyPair(keyPair) {
  if (!keyPair.controller) {
    throw new Error('"controller" property in keyPair is required.');
  }
  if (!keyPair.id) {
    throw new Error('"id" property in keyPair is required.');
  }
  if (keyPair.type === w3cIssuer.VerificationType.Bls12381G2Key2020) {
    if (!keyPair.privateKeyBase58) {
      throw new Error('"privateKeyBase58" property in keyPair is required.');
    }
    if (!keyPair.publicKeyBase58) {
      throw new Error('"publicKeyBase58" property in keyPair is required.');
    }
  }
}
__name(_checkKeyPair, "_checkKeyPair");
function _getId(obj) {
  if (typeof obj === "string") {
    return obj;
  }
  if (!("id" in obj)) {
    return;
  }
  return obj.id;
}
__name(_getId, "_getId");
const mustHaveType = [
  "proof",
  "credentialStatus"
];
const dateRegex = new RegExp("-?([1-9][0-9]{3,}|0[0-9]{3})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])T(([01][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9](.[0-9]+)?|(24:00:00(.0+)?))(Z|(\\+|-)((0[0-9]|1[0-3]):[0-5][0-9]|14:00))?");
function assertDateString({ credential, prop }) {
  const value = credential[prop];
  if (!dateRegex.test(value)) {
    throw new Error(`"${prop}" must be a valid date: ${value}`);
  }
}
__name(assertDateString, "assertDateString");
function assertCredentialContext(credential) {
  if (credential["@context"][0] !== w3cContext.CredentialContextVersion.v1) {
    throw new Error(`The first element of '@context' must be '${w3cContext.CredentialContextVersion.v1}'`);
  }
}
__name(assertCredentialContext, "assertCredentialContext");
function _checkCredential(credential, now = /* @__PURE__ */ new Date(), mode = "verify") {
  if (typeof now === "string") {
    now = new Date(now);
  }
  if (jsonld__namespace.getValues(credential, "@context").length < 1) {
    throw new Error('"@context" property is required.');
  }
  assertCredentialContext(credential);
  if (!credential.type) {
    throw new Error('"type" property is required.');
  }
  if (!jsonld__namespace.getValues(credential, "type").includes("VerifiableCredential")) {
    throw new Error('"type" must include `VerifiableCredential`.');
  }
  _checkCredentialSubjects(credential);
  if (!credential.issuer) {
    throw new Error('"issuer" property is required.');
  }
  if (jsonld__namespace.getValues(credential, "issuer").length > 1) {
    throw new Error('"issuer" property can only have one value.');
  }
  if ("issuer" in credential) {
    const issuer = _getId(credential.issuer);
    if (!issuer) {
      throw new Error(`"issuer" id is required.`);
    }
    _validateUriId({
      id: issuer,
      propertyName: "issuer"
    });
  }
  if (!credential.issuanceDate) {
    throw new Error('"issuanceDate" property is required.');
  }
  assertDateString({
    credential,
    prop: "issuanceDate"
  });
  if (jsonld__namespace.getValues(credential, "issuanceDate").length > 1) {
    throw new Error('"issuanceDate" property can only have one value.');
  }
  if ("expirationDate" in credential) {
    assertDateString({
      credential,
      prop: "expirationDate"
    });
    if (mode === "verify") {
      if (now > new Date(credential.expirationDate)) {
        console.warn("Credential has expired.");
      }
    }
  }
  if (mode === "verify") {
    const issuanceDate = new Date(credential.issuanceDate);
    if (now < issuanceDate) {
      throw new Error(`The current date time (${now.toISOString()}) is before the "issuanceDate" (${credential.issuanceDate}).`);
    }
    if (!credential.proof) {
      throw new Error('"proof" property is required.');
    }
    if (jsonld__namespace.getValues(credential, "proof").length > 1) {
      throw new Error('"proof" property can only have one value.');
    }
    const proofType = jsonld__namespace.getValues(credential, "proof")[0].type;
    if (!types.proofTypeMapping[proofType]) {
      throw new Error('"proof" type is not of BbsBlsSignature2020.');
    }
  }
  if (mode === "sign") {
    if (credential.proof) {
      throw new Error('"proof" property is already there.');
    }
    if (credential.id) {
      throw new Error('"id" is a defined field and should not be set by the user.');
    }
  }
  credentialStatus.assertCredentialStatuses(credential, mode);
  for (const prop of mustHaveType) {
    if (prop in credential) {
      const _value = credential[prop];
      if (Array.isArray(_value)) {
        _value.forEach((entry) => _checkTypedObject(entry, prop));
        continue;
      }
      _checkTypedObject(_value, prop);
    }
  }
}
__name(_checkCredential, "_checkCredential");
function _checkTypedObject(obj, name) {
  if (!isObject(obj)) {
    throw new Error(`property "${name}" must be an object.`);
  }
  if (_emptyObject(obj)) {
    throw new Error(`property "${name}" can not be an empty object.`);
  }
  if (!("type" in obj)) {
    throw new Error(`property "${name}" must have property type.`);
  }
}
__name(_checkTypedObject, "_checkTypedObject");
function _checkCredentialSubjects(credential) {
  if (!credential?.credentialSubject) {
    throw new Error('"credentialSubject" property is required.');
  }
  if (Array.isArray(credential?.credentialSubject)) {
    credential?.credentialSubject.map((subject) => _checkCredentialSubject({
      subject
    }));
    return;
  }
  _checkCredentialSubject({
    subject: credential?.credentialSubject
  });
}
__name(_checkCredentialSubjects, "_checkCredentialSubjects");
function _checkCredentialSubject(subject) {
  if (isObject(subject) === false) {
    throw new Error('"credentialSubject" must be a non-null object.');
  }
  if (_emptyObject(subject)) {
    throw new Error('"credentialSubject" must make a claim.');
  }
  if (subject.id) {
    _validateUriId({
      id: subject.id,
      propertyName: "credentialSubject.id"
    });
  }
}
__name(_checkCredentialSubject, "_checkCredentialSubject");
function isObject(obj) {
  if (obj === null) {
    return false;
  }
  if (typeof obj === "object") {
    return true;
  }
  return false;
}
__name(isObject, "isObject");
function _emptyObject(obj) {
  if (!isObject(obj)) {
    return true;
  }
  return Object.keys(obj).length === 0;
}
__name(_emptyObject, "_emptyObject");
function _validateUriId({ id, propertyName }) {
  let parsed;
  try {
    parsed = new URL(id);
  } catch (e) {
    const error = new Error(`"${propertyName}" must be a URI: "${id}".`);
    throw error;
  }
  if (!parsed.protocol) {
    throw new Error(`"${propertyName}" must be a URI: "${id}".`);
  }
}
__name(_validateUriId, "_validateUriId");
const _checkCredentialStatus = /* @__PURE__ */ __name((credentialStatus, mode = "verify") => {
  const { type } = credentialStatus ?? {};
  if (type === "StatusList2021Entry") {
    w3cCredentialStatus.assertStatusList2021Entry(credentialStatus);
  } else if (type === "TransferableRecords") {
    w3cCredentialStatus.assertTransferableRecords(credentialStatus, mode);
  } else {
    w3cCredentialStatus.assertCredentialStatusType(type);
  }
}, "_checkCredentialStatus");
const prefilCredentialId = /* @__PURE__ */ __name((credential) => {
  credential.id = `urn:bnid:_:${uuid.v7()}`;
  if (credential?.credentialStatus?.type === "TransferableRecords") {
    credential.credentialStatus = {
      ...credential.credentialStatus,
      tokenId: crypto.createHash("sha256").update(credential.id).digest("hex")
    };
  }
  return credential;
}, "prefilCredentialId");

exports._checkCredential = _checkCredential;
exports._checkCredentialStatus = _checkCredentialStatus;
exports._checkKeyPair = _checkKeyPair;
exports._validateUriId = _validateUriId;
exports.prefilCredentialId = prefilCredentialId;
